'use client';

import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_COPILOT_MODE,
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type CopilotMode,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import {
  ChatComposerStatusLine,
  getNextCycledCopilotMode,
  toasterApi,
  useChatFeedback,
  type ChatComposerStatusLineData,
  type SlashCommandItem,
} from '@terra/ui-design-system';
import { useSkills } from '@terra/ui-skills';
import { useTranslations } from 'next-intl';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { useTerminalApprovalStore } from '../../store/terminalApprovalStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useCreateNewChatTab } from './actions/useCreateNewChatTab';
import {
  formatTokenCount,
  type TokenUsageEstimate,
} from './token-usage/tokenUsageEstimate';
import {
  resolveCoPilotExportErrorMessage,
  useCoPilotExportChatTitle,
  useCoPilotSessionExport,
} from './session-export';
import { resolveCoPilotTraceExecutionState } from '../../utils/resolve-co-pilot-trace-status';

function extractLatestTraceId(messages: TerraUIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    for (const part of msg.parts ?? []) {
      if (part.type === TerraUIMessageDataPartName && part.data) {
        const record = part.data as { type?: unknown; traceId?: unknown };

        if (
          record.type === DataMessageTypeEnum.TraceMetadata &&
          typeof record.traceId === 'string' &&
          record.traceId.length > 0
        ) {
          return record.traceId;
        }
      }
    }
  }

  return null;
}

function readOptionalCacheHitRatioPercent(
  messages: TerraUIMessage[],
): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    for (const part of msg.parts ?? []) {
      if (part.type === TerraUIMessageDataPartName && part.data) {
        const record = part.data as {
          type?: unknown;
          cacheHitRatio?: unknown;
          cacheHitRatioPercent?: unknown;
        };

        if (record.type === DataMessageTypeEnum.TokenUsage) {
          if (
            typeof record.cacheHitRatioPercent === 'number' &&
            Number.isFinite(record.cacheHitRatioPercent)
          ) {
            return record.cacheHitRatioPercent;
          }

          if (
            typeof record.cacheHitRatio === 'number' &&
            Number.isFinite(record.cacheHitRatio)
          ) {
            return record.cacheHitRatio <= 1
              ? record.cacheHitRatio * 100
              : record.cacheHitRatio;
          }
        }
      }
    }
  }

  return null;
}

function readOptionalModelName(messages: TerraUIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    for (const part of msg.parts ?? []) {
      if (part.type === TerraUIMessageDataPartName && part.data) {
        const record = part.data as {
          type?: unknown;
          model?: unknown;
          modelName?: unknown;
        };

        if (
          record.type === DataMessageTypeEnum.TokenUsage ||
          record.type === DataMessageTypeEnum.TraceMetadata
        ) {
          if (typeof record.modelName === 'string' && record.modelName.trim()) {
            return record.modelName.trim();
          }

          if (typeof record.model === 'string' && record.model.trim()) {
            return record.model.trim();
          }
        }
      }
    }
  }

  return null;
}

export function useCopilotSlashCommandsAndStatusLine({
  chatId,
  appTargetId,
  targetId,
  messages,
  tokenUsageEstimate,
  workingCount,
  handleSend,
  onToggleTokenUsage,
}: {
  chatId: string;
  appTargetId: string | undefined;
  targetId: string | undefined;
  messages: TerraUIMessage[];
  tokenUsageEstimate: TokenUsageEstimate;
  workingCount: number;
  handleSend: (message: TerraUIMessage) => Promise<void> | void;
  onToggleTokenUsage?: () => void;
}) {
  const tMode = useTranslations('chat.copilot.mode');
  const tToolbar = useTranslations('chat.copilot.toolbar');
  const tSlash = useTranslations('chat.copilot.slashCommands');
  const currentMode = useChatInstancesStore(
    (state) => state.configs.get(chatId)?.mode ?? DEFAULT_COPILOT_MODE,
  );
  const setChatMode = useChatInstancesStore((state) => state.setChatMode);
  const setTerminalPolicy = useTerminalApprovalStore(
    (state) => state.setPolicy,
  );
  const { openOrFocusTab } = useMainContentTabs();
  const { createNewChatTab } = useCreateNewChatTab();
  const { onFeedbackClick } = useChatFeedback();

  const { skills } = useSkills(
    appTargetId ? { applicationId: appTargetId } : {},
  );

  const exportChatTitle = useCoPilotExportChatTitle(chatId);
  const chatStatus = useChatInstancesStore((state) =>
    state.getChatStatus(chatId),
  );
  const exportStatus = useMemo(
    () =>
      resolveCoPilotTraceExecutionState({
        chatStatus,
        history: messages,
      }).traceStatus,
    [chatStatus, messages],
  );
  const { exportSession } = useCoPilotSessionExport({
    threadId: chatId,
    applicationId: targetId,
    messages,
    status: exportStatus,
    chatTitle: exportChatTitle,
  });

  const switchMode = useCallback(
    (mode: CopilotMode) => {
      setChatMode(chatId, mode);
      toasterApi.success({
        title: tMode('switchedTo', { mode: tMode(`${mode}Label`) }),
      });
    },
    [chatId, setChatMode, tMode],
  );

  const handleCycleMode = useCallback(() => {
    const nextMode = getNextCycledCopilotMode(currentMode);

    switchMode(nextMode);
  }, [currentMode, switchMode]);

  const slashCommands = useMemo<SlashCommandItem[]>(() => {
    const builtIn: SlashCommandItem[] = [
      {
        id: 'mode-agent',
        name: 'agent',
        label: tMode('agentLabel'),
        description: tMode('agentDescription'),
        kind: 'mode',
        modeTarget: 'agent',
        onExecute: () => {
          switchMode('agent');
        },
      },
      {
        id: 'mode-plan',
        name: 'plan',
        label: tMode('planLabel'),
        description: tMode('planDescription'),
        kind: 'mode',
        modeTarget: 'plan',
        onExecute: () => {
          switchMode('plan');
        },
      },
      {
        id: 'mode-ask',
        name: 'ask',
        label: tMode('askLabel'),
        description: tMode('askDescription'),
        kind: 'mode',
        modeTarget: 'ask',
        onExecute: () => {
          switchMode('ask');
        },
      },
      {
        id: 'mode-auto',
        name: 'auto',
        label: tSlash('autoLabel'),
        description: tSlash('autoDescription'),
        kind: 'mode',
        onExecute: () => {
          setChatMode(chatId, 'agent');
          setTerminalPolicy('auto-review');
          toasterApi.success({
            title: tMode('switchedTo', { mode: tSlash('autoLabel') }),
          });
        },
      },
      {
        id: 'action-compact',
        name: 'compact',
        label: tSlash('compactLabel'),
        description: tSlash('compactDescription'),
        kind: 'action',
        onExecute: ({ rawArgs }) => {
          const text = rawArgs ? `/compact ${rawArgs}` : '/compact';

          void handleSend({
            id: uuidv4(),
            role: 'user',
            parts: [{ type: 'text', text }],
          });
        },
      },
      {
        id: 'action-clear',
        name: 'clear',
        label: tSlash('clearLabel'),
        description: tSlash('clearDescription'),
        kind: 'action',
        aliases: ['new'],
        onExecute: () => {
          createNewChatTab({});
        },
      },
      {
        id: 'action-skills',
        name: 'skills',
        label: tSlash('skillsLabel'),
        description: tSlash('skillsDescription'),
        kind: 'action',
        onExecute: () => {
          openOrFocusTab({
            id: 'skills-list',
            title: tSlash('skillsLabel'),
            type: 'skills-list',
          });
        },
      },
      {
        id: 'action-export',
        name: 'export',
        label: tToolbar('exportTrace'),
        description: tSlash('exportDescription'),
        kind: 'action',
        argumentHint: tSlash('exportArgumentHint'),
        onExecute: async ({ rawArgs }) => {
          const normalizedFormat = rawArgs.trim().toLowerCase();
          const format =
            normalizedFormat === 'csv' || normalizedFormat === 'har'
              ? normalizedFormat
              : 'markdown';

          try {
            await exportSession(format);
          } catch (error: unknown) {
            toasterApi.error({
              title: resolveCoPilotExportErrorMessage({
                error,
                fallback: tToolbar('exportError'),
                emptyMessage: tToolbar('exportEmpty'),
              }),
            });
          }
        },
      },
      {
        id: 'action-feedback',
        name: 'feedback',
        label: tSlash('feedbackLabel'),
        description: tSlash('feedbackDescription'),
        kind: 'action',
        onExecute: () => {
          const latestTraceId = extractLatestTraceId(messages);

          if (!latestTraceId) {
            return;
          }

          onFeedbackClick({
            traceId: latestTraceId,
            actionType: 'negative',
          });
        },
      },
    ];

    const skillCommands: SlashCommandItem[] = skills
      .filter((skill) => Boolean(skill.slug))
      .map((skill) => ({
        id: `skill-${skill.skillId}`,
        name: skill.slug,
        label: skill.name || skill.slug,
        description:
          skill.description || tSlash('runSkillFallback', { slug: skill.slug }),
        kind: 'skill' as const,
        skillSlug: skill.slug,
        argumentHint: tSlash('skillArgumentHint'),
        onExecute: ({ rawArgs }) => {
          const text = rawArgs
            ? `/${skill.slug} ${rawArgs}`
            : tSlash('useSkillPrompt', { slug: skill.slug });

          void handleSend({
            id: uuidv4(),
            role: 'user',
            parts: [{ type: 'text', text }],
          });
        },
      }));

    return [...builtIn, ...skillCommands];
  }, [
    chatId,
    createNewChatTab,
    exportSession,
    handleSend,
    messages,
    onFeedbackClick,
    openOrFocusTab,
    setChatMode,
    setTerminalPolicy,
    skills,
    switchMode,
    tMode,
    tSlash,
    tToolbar,
  ]);

  const statusLineData = useMemo<ChatComposerStatusLineData>(() => {
    const contextPercent =
      tokenUsageEstimate.effectiveWindowTokens > 0
        ? Math.min(Math.round(tokenUsageEstimate.usedFraction * 100), 100)
        : null;

    return {
      mode: currentMode,
      modeLabel: tMode(`${currentMode}Label`),
      contextPercent,
      contextUsedTokensFormatted: formatTokenCount(
        tokenUsageEstimate.orchestratorTokens,
      ),
      contextWindowTokensFormatted: formatTokenCount(
        tokenUsageEstimate.effectiveWindowTokens,
      ),
      isOverCompactionTrigger: tokenUsageEstimate.isOverTrigger,
      cacheHitRatioPercent: readOptionalCacheHitRatioPercent(messages),
      backgroundTasksCount: workingCount,
      modelName: readOptionalModelName(messages),
    };
  }, [currentMode, messages, tMode, tokenUsageEstimate, workingCount]);

  const statusLineContent = useMemo(
    () => (
      <ChatComposerStatusLine
        data={statusLineData}
        onClickContext={onToggleTokenUsage}
      />
    ),
    [onToggleTokenUsage, statusLineData],
  );

  return {
    slashCommands,
    handleCycleMode,
    statusLineData,
    statusLineContent,
  };
}
